import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { GfTitleService } from '../../../services/core.title.service';
import * as _ from 'lodash';

import { UiService } from '../../../services/core.ui.service';
import { FilterService } from '../../../services/core.filter.service';
import { GfTranslateService } from '../../../services/core.translate.service';
import { UserRest } from '../../../services/core.user.rest.service';
import { UserService } from '../../../services/core.user.service';
import { StateService } from '@uirouter/angular';

@Component({
  selector: 'user-notifications-view',
  template: require('./user.notifications.component.html')
})
export class UserNotificationsViewComponent implements OnInit {
  @Input() user: any;

  // Properties
  loadingWall = false;
  filters: any;
  noscroll = false;
  sortFields = {
    'postDate': 'Notification Date',
    'user': 'Posted By',
    'itemSection': 'Item Section',
    'itemRefId': 'Item Reference',
    'project': 'Project'
  };

  // Sorting and paging
  sort: any;
  paging: any;

  // Data
  wall: any[] | false = false;
  lastSeenWallMessage = 0;

  constructor(
    private stateService: StateService,
    
    private titleService: GfTitleService,
    private uiService: UiService,
    private filterService: FilterService,
    private translateService: GfTranslateService,
    private userRest: UserRest,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.initComponent();
  }

  private initComponent(): void {
    this.titleService.setTitle(
      this.user?.unixName || '', this.translateService.translate('User Notifications')
    );

    // If the current session user differs and not site admin, redirect
    const sessionUnix = this.userService.session.getUnixName();
    if (sessionUnix !== this.user.unixName) {
      if (!this.userService.session.isSiteAdmin()) {
        this.stateService.go('home.user', {unixName: this.user.unixName}); // Or your desired route
        return;
      }
    }

    // Get initial sorting/paging from FilterService
    this.sort = this.filterService.getSortValues();
    this.paging = this.filterService.getPagingValues();

    // Prepare filters
    this.filters = this.setFilterValues();

    // Load data
    this.loadWallMessages();
  }

  /* FILTER & SORT */
  onApplyFilters($event: any): void {
    this.paging.page_num = 1;
    this.applyFilters();
  }

  onResetFilters($event: any): void {
    this.paging.page_num = 1;
    this.resetFilters();
    this.applyFilters();
  }

  setSort(params: any): void {
    this.sort = {
      sort_field: _.get(params, 'key', 'releaseDate'),
      sort_dir: _.get(params, 'sortDir', 'desc')
    };
    this.paging.page_num = 1;
    this.applyFilters();
  }

  updateFilters(changes: any): void {
    this.applyFilters(changes);
  }

  setPaging(params: any): void {
    this.paging = _.assign(this.paging, params);
    this.applyFilters();
  }

  /* NAVIGATION */
  goToItem(wallpost: any): void {
    if (wallpost.itemSection && wallpost.itemRefId) {
      this.uiService.state.goToItem(
        wallpost.itemSection,
        wallpost.itemRefId,
        null,
        wallpost?.project?.unixName
      );
    }
  }

  /* HELPER METHODS */
  private applyFilters(overrides: any = {}): void {
    const filterVals = this.filterService.getFilterValues(
      _.get(this.filters, 'main_fields', [])
    );

    const newParams = _.pickBy(
      Object.assign(
        { unixName: this.user?.unixName },
        this.sort,
        this.paging,
        filterVals,
        overrides
      ),
      (value) => value !== undefined && value !== null && value !== ''
    );

    this.stateService.go('.', newParams)
    .then(() => {
      this.loadWallMessages();
    });
  }

  private setFilterValues(): any {
    return {
      main_fields: this.filterService.setFilterValues(this.getBaseFilters())
    };
  }

  private resetFilters(): void {
    this.filters = {
      main_fields: this.getBaseFilters()
    };
  }

  private getBaseFilters(): any[] {
    const elements = [
      { id: '', label: 'Any' },
      { id: 'forum', label: 'Forum' },
      { id: 'trackeritem', label: 'Trackeritem' },
      { id: 'build', label: 'Build' },
      { id: 'project', label: 'Project' }
    ];

    return [
      { alias: 'user', fieldName: 'Posted By', value: '', fieldType: 4, isExtraField: false },
      {
        alias: 'itemSection',
        fieldName: 'Item Section',
        value: { id: '', label: 'Any' },
        fieldType: 1,
        isExtraField: false,
        fieldElements: elements
      },
      { alias: 'itemRefId', fieldName: 'Item Reference Id', value: '', fieldType: 4, isExtraField: false },
      { alias: 'comment', fieldName: 'Comment', value: '', fieldType: 4, isExtraField: false },
      { alias: 'project', fieldName: 'Project', value: '', fieldType: 4, isExtraField: false }
    ];
  }

  /* LOADING DATA */
  private loadWallMessages(): void {
    this.wall = false;

    this.userRest.getUserPreference(this.user.unixName, 'toaster-since', { defaultValue: 0 })
      .then(resource => {
        this.lastSeenWallMessage = parseInt(_.get(resource, 'preferenceValue', 0), 10) || 0;
        this.getWallPosts();
      })
      .catch(response => {
        if (response.status !== 404) {
          const msg = this.translateService.translate('There was a problem loading wall preference');
          this.uiService.message.showWarning(msg, 'Data Error');
        }
        this.wall = [];
      });
  }

  private getWallPosts(): void {
    const queryParams = this.stateService.params || {};
    const params = _.pickBy(queryParams, (v) => v !== '');

    this.userRest.getWall(this.user.unixName, params)
      .then(resource => {
        const newItems = _.get(resource, 'items', []);
        const newMaxId = this.getLatestMessageId(newItems);

        if (this.lastSeenWallMessage < newMaxId) {
          this.setLastSeenMessage(newMaxId);
          this.lastSeenWallMessage = newMaxId;
        }

        this.wall = this.transformMessages(newItems);
      })
      .catch(() => {
        const msg = this.translateService.translate('There was a problem loading wall posts');
        this.uiService.message.showWarning(msg, 'Data Error');
      });
  }

  private setLastSeenMessage(id: any): void {
    this.userRest.saveUserPreference(this.userService.session.getUnixName(), 'toaster-since', id)
    .then(() => {}, () => {});
  }

  private getLatestMessageId(items: any[]): number {
    return _.max(_.map(items, 'id')) || 0;
  }

  private transformMessages(list: any[]): any[] {
    const thisUnixName = this.userService.session.getUnixName();
    return _.map(list, (msg: any) => {
      const userMatches = msg.comment.match(/@(\w+)\b/g);
      if (!_.isEmpty(userMatches)) {
        _.each(userMatches, (mention: string) => {
          const mentionedUser = mention.replace('@', '');
          let mentionedUserUrl = '';

          if (mentionedUser === thisUnixName) {
            mentionedUserUrl = `<strong>${mention}</strong>`;
          } else {
            mentionedUserUrl = `<a href="/user/${mentionedUser}" target="_blank" title="Go to this user">${mention}</a>`;
          }
          msg.comment = msg.comment.split(mention).join(mentionedUserUrl);
        });
      }
      return msg;
    });
  }
}
